import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDaysStr, dayLongLabel, monthLabel, parseLocalDate, todayStr, weekStartStr } from './AgendaResponsiveView';

// Cuántas semanas se dibujan a cada lado del ancla. Con esto la tira cubre
// ~3 meses para cada lado, que es hasta donde tiene sentido llegar arrastrando;
// más lejos que eso el ancla se recalcula sola.
const SEMANAS_A_CADA_LADO = 12;

const SECTION_META = {
  events: { label: 'Eventos', tone: 'green' },
  armados: { label: 'Armados', tone: 'amber' },
  desarmados: { label: 'Desarmados', tone: 'red' },
  tasks: { label: 'Tareas', tone: 'neutral' },
};

function dayChipLabel(dateStr) {
  const date = parseLocalDate(dateStr);
  const day = new Intl.DateTimeFormat('es-UY', { weekday: 'short' }).format(date).replace('.', '').toUpperCase();
  // El nombre del mes lo pone el rótulo de arriba; acá alcanza con marcar
  // dónde cambia, que arrastrando es lo único que no se ve.
  return { day, number: date.getDate(), empiezaMes: date.getDate() === 1 };
}

function diasAlrededorDe(dateStr) {
  const primero = addDaysStr(weekStartStr(dateStr), -SEMANAS_A_CADA_LADO * 7);
  const total = (SEMANAS_A_CADA_LADO * 2 + 1) * 7;
  return Array.from({ length: total }, (_, i) => addDaysStr(primero, i));
}

function AssignmentPills({ people = [], totalPeople = 0, warning = false }) {
  const hidden = Math.max(0, totalPeople - people.length);
  if (warning) {
    return <span className="agm-warning-pill">△ sin asignar</span>;
  }
  if (!people.length && !totalPeople) return null;
  return (
    <div className="agm-person-pills">
      {people.map(person => (
        <span key={person.id} className={`agm-person-pill${person.isMe ? ' agm-person-pill--me' : ''}`}>
          {person.name}
          {person.role && <em>{person.role}</em>}
        </span>
      ))}
      {hidden > 0 && <span className="agm-more-pill">+{hidden}</span>}
    </div>
  );
}

function AgendaCard({ card, onOpen }) {
  return (
    <button type="button" className={`agm-card agm-card--${card.kind}`} onClick={() => onOpen(card)}>
      <span className="agm-card-main">
        <strong>{card.title}</strong>
        {card.subtitle && <small>{card.subtitle}</small>}
        <AssignmentPills people={card.people} totalPeople={card.totalPeople} warning={card.warning} />
      </span>
      <span className="agm-card-side">
        {card.time && <strong>{card.time}</strong>}
        {card.badge && (
          <span className={`agm-cl-badge agm-cl-${card.badge.tone}`}>{card.badge.label}</span>
        )}
        <span aria-hidden="true">›</span>
      </span>
    </button>
  );
}

function AgendaSection({ type, items, isOpen, onToggle, onOpenCard, onAdd }) {
  const meta = SECTION_META[type];
  const warningCount = items.filter(item => item.warning).length;
  const addLabel = {
    armados: '+ armado',
    desarmados: '+ desarmado',
    tasks: '+ tarea',
  }[type];
  return (
    <section className={`agm-section agm-section--${meta.tone}${isOpen ? ' is-open' : ' is-collapsed'}`}>
      <div className="agm-section-head">
        <button
          type="button"
          className="agm-section-title"
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          <span aria-hidden="true">{isOpen ? '⌄' : '›'}</span>
          <strong>{meta.label}</strong>
          <em>{items.length}</em>
          {warningCount > 0 && <mark>{warningCount} △</mark>}
        </button>
        {onAdd && addLabel && (
          <button type="button" className="agm-section-add" onClick={onAdd}>
            {addLabel}
          </button>
        )}
      </div>
      {isOpen && (
        items.length ? (
          <div className="agm-card-list">
            {items.map(card => <AgendaCard key={card.id} card={card} onOpen={onOpenCard} />)}
          </div>
        ) : (
          <p className="agm-empty-section">Nada para este dia</p>
        )
      )}
    </section>
  );
}

export default function AgendaMobileWeekView({ selectedDate, volverAlDia = 0, countsByDate, daySummary, onSelectDate, onOpenCard, onAddTask, onAddOperation }) {
  const today = todayStr();

  // La tira arranca centrada en el día elegido y se puede arrastrar para ver
  // otras semanas sin tocar las flechas. El ancla sólo se mueve si el día
  // elegido se sale del tramo dibujado, así que tocar un día no reacomoda la
  // tira bajo el dedo.
  const [ancla, setAncla] = useState(() => weekStartStr(selectedDate));
  const weekDays = useMemo(() => diasAlrededorDe(ancla), [ancla]);
  useEffect(() => {
    if (selectedDate < weekDays[0] || selectedDate > weekDays[weekDays.length - 1]) {
      setAncla(weekStartStr(selectedDate));
    }
  }, [selectedDate, weekDays]);

  const stripRef = useRef(null);
  const yaCentroUnaVez = useRef(false);
  const centrarEn = useCallback(fecha => {
    const strip = stripRef.current;
    const chip = strip?.querySelector(`[data-date="${fecha}"]`);
    if (!strip || !chip) return;
    // Por rects y no por offsetLeft: offsetLeft depende de quién sea el
    // offsetParent, que cambia con el CSS y silenciosamente da otro número.
    const caja = chip.getBoundingClientRect();
    const marco = strip.getBoundingClientRect();
    const destino = strip.scrollLeft + (caja.left - marco.left) - (marco.width - caja.width) / 2;
    strip.scrollTo({
      left: Math.max(0, destino),
      behavior: yaCentroUnaVez.current ? 'smooth' : 'auto',
    });
    yaCentroUnaVez.current = true;
  }, []);

  // `volverAlDia` cubre el caso en que el día no cambia pero igual hay que
  // volver: arrastraste la tira lejos y tocaste "Hoy" estando ya en hoy.
  useEffect(() => {
    centrarEn(selectedDate);
  }, [selectedDate, weekDays, volverAlDia, centrarEn]);

  // Rótulo del mes: sigue a lo que estás mirando, no al día elegido. Sin esto,
  // arrastrando a mitad de mes no hay ninguna referencia de dónde estás hasta
  // que tocás un día.
  const [mesVisible, setMesVisible] = useState(() => selectedDate.slice(0, 7));
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    let pedido = null;
    const medir = () => {
      pedido = null;
      const chips = strip.children;
      if (chips.length < 2) return;
      // El paso se mide entre dos chips en vez de leer el gap del CSS: así
      // sigue andando si cambia el ancho o el gap en otra media query.
      const paso = chips[1].getBoundingClientRect().left - chips[0].getBoundingClientRect().left;
      if (paso <= 0) return;
      const centro = strip.scrollLeft + strip.clientWidth / 2;
      const i = Math.min(chips.length - 1, Math.max(0, Math.round(centro / paso - 0.5)));
      const fecha = chips[i].dataset.date;
      if (fecha) setMesVisible(fecha.slice(0, 7));
    };
    const alScrollear = () => { if (!pedido) pedido = requestAnimationFrame(medir); };
    medir();
    strip.addEventListener('scroll', alScrollear, { passive: true });
    return () => {
      strip.removeEventListener('scroll', alScrollear);
      if (pedido) cancelAnimationFrame(pedido);
    };
  }, [weekDays]);

  const [openSections, setOpenSections] = useState({
    events: true,
    armados: true,
    desarmados: true,
    tasks: true,
  });

  function toggleSection(type) {
    setOpenSections(prev => ({ ...prev, [type]: !prev[type] }));
  }

  return (
    <div className="agm-week-view">
      <div className="agm-strip-month" aria-live="polite">{monthLabel(mesVisible)}</div>

      <div className="agm-week-strip" ref={stripRef} aria-label="Dias">
        {weekDays.map(date => {
          const label = dayChipLabel(date);
          const counts = countsByDate[date] || {};
          return (
            <button
              key={date}
              type="button"
              data-date={date}
              className={[
                'agm-day-chip',
                date === selectedDate ? 'selected' : '',
                date === today ? 'today' : '',
                label.empiezaMes ? 'mes-nuevo' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => { onSelectDate(date); centrarEn(date); }}
            >
              <span>{label.day}</span>
              <strong>{label.number}</strong>
              {counts.total > 0 && <i aria-label={`${counts.total} items`} />}
              {counts.warnings > 0 && <b aria-label={`${counts.warnings} alertas`} />}
            </button>
          );
        })}
      </div>

      <h2 className="agm-day-heading">
        {dayLongLabel(selectedDate)}
        {selectedDate === today && <span>Hoy</span>}
      </h2>

      <AgendaSection type="events" items={daySummary.events} isOpen={openSections.events} onToggle={() => toggleSection('events')} onOpenCard={onOpenCard} />
      <AgendaSection type="armados" items={daySummary.armados} isOpen={openSections.armados} onToggle={() => toggleSection('armados')} onOpenCard={onOpenCard} onAdd={() => onAddOperation?.('armado')} />
      <AgendaSection type="desarmados" items={daySummary.desarmados} isOpen={openSections.desarmados} onToggle={() => toggleSection('desarmados')} onOpenCard={onOpenCard} onAdd={() => onAddOperation?.('desarmado')} />
      <AgendaSection type="tasks" items={daySummary.tasks} isOpen={openSections.tasks} onToggle={() => toggleSection('tasks')} onOpenCard={onOpenCard} onAdd={onAddTask} />
    </div>
  );
}
